/**
 * ░░░░░▄▀▀▀▄░░░░░░░░░░░░░░░░░
 * ▄███▀░◐░░░▌░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▐░░░░░▐░░░░░░░░░░░░░░░░
 * ░░░░▌░░░░░▐▄▄░░░░░░░░░░░░░░
 * ░░░░▌░░░░▄▀▒▒▀▀▀▀▄░░░░░░░░░
 * ░░░▐░░░░▐▒▒▒▒▒▒▒▒▀▀▄░░░░░░░
 * ░░░▐░░░░▐▄▒▒▒▒▒▒▒▒▒▒▀▄░░░░░
 * ░░░░▀▄░░░░▀▄▒▒▒▒▒▒▒▒▒▒▀▄░░░
 * ░░░░░░▀▄▄▄▄▄█▄▄▄▄▄▄▄▄▄▄▄▀▄░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░░░▌▌░▌▌░░░░░░░░░░░
 * ░░░░░░░░░▄▄▌▌▄▌▌░░░░░░░░░░░
 *
 * MIGRATION NOTICE
 *
 * We are in the process of re-writing our components using function components
 * and React Hooks, rather than extending Component.
 *
 * We have found that hooks make our components better, and - once you get the
 * hang of them - produce fewer bugs and edge cases than conventional lifecycle
 * methods (like componentDidMount and getDerivedStateFromProps).
 *
 * However, hooks can’t be used from class components. Therefore, we are trying
 * to eliminate class components wherever we can.
 *
 * If you’re changing this file, please consider contributing to this effort by
 * changing this component to be a function that uses hooks, and then removing
 * this notice.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';

import theme from 'site-react/theme';

const fullScreenBaseStyles = css`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
`;

const wrapperStyle = css`
  background: ${theme.colors.white};
  bottom: 0;
  left: 0;
  opacity: 0;
  overflow: hidden;
  position: fixed;
  top: 60px;
  transform: translateY(100%);
  transition: transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  width: 100%;
  z-index: ${theme.zIndex.drawer};
`;

const wrapperVisibleStyle = css`
  opacity: 1;
  transform: translateY(0%);
`;

const backdropStyle = css`
  ${fullScreenBaseStyles}
  background-color: ${theme.colors.black};
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: -9999;
  /* NB. when we don't have this z-index hack in place, the backdrop
     * element occludes the hamburger icon that opens the menu! */
`;

const backdropVisibleStyle = css`
  opacity: 0.7;
  z-index: ${theme.zIndex.drawer};
`;

class Drawer extends Component {
  /**
   * Drawer is a reusable panel
   * revealing component
   *
   * @param {any} props
   */
  constructor(props) {
    super(props);
    this.state = {
      isVisible: props.isVisible,
    };

    this.onBackdropClick = this.onBackdropClick.bind(this);
  }

  componentDidMount() {
    this.domBody = document.body;
    this.overlayClass = 'body--overlayed';
  }

  componentWillReceiveProps(nextProps) {
    const { isVisible } = this.state;

    if (nextProps.isVisible === isVisible) return;

    // Stops background scrolling while drawer is open
    if (nextProps.isVisible) {
      this.domBody.classList.add(this.overlayClass);
    } else {
      this.domBody.classList.remove(this.overlayClass);
    }

    this.setState({
      isVisible: nextProps.isVisible,
    });
  }

  componentWillUnmount() {
    this.domBody.classList.remove(this.overlayClass);
  }

  /**
   * On clicking the backdrop
   * event hook
   */
  onBackdropClick() {
    const { onClose } = this.props;

    onClose();
    this.setState({
      isVisible: false,
    });
  }

  render() {
    const { children } = this.props;
    const { isVisible } = this.state;

    return (
      <div>
        <div
          className={cx('test-Drawer-backdrop', backdropStyle, {
            [backdropVisibleStyle]: isVisible,
          })}
          onClick={this.onBackdropClick}
        />

        <div
          className={cx(wrapperStyle, {
            [wrapperVisibleStyle]: isVisible,
          })}
        >
          {children}
        </div>
      </div>
    );
  }
}

Drawer.defaultProps = {
  children: null,
  isVisible: false,
  onClose: () => {},
};

Drawer.propTypes = {
  children: PropTypes.node,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default Drawer;
